/** Fixed scope attribute value for every TipTap HTML document editor instance. */
export const EDITOR_SCOPE_ID = 'editor';

/**
 * `:root`, `html` and `body` all name the document root. Prefixed as a descendant they can
 * never match anything, because <html> is not inside another element - so a stylesheet's
 * `:root { --primary-color: … }` silently defines nothing in the editor and every rule reading
 * that variable falls back, which is how the editor comes to disagree with the print.
 */
const DOCUMENT_ROOT = /^(?::root|html|body)(?![\w-])\s*/i;

/** A selector fragment that continues the same element rather than descending into it. */
const CONTINUES_ELEMENT = /^[.#[:]/;

/** `:where()` takes no pseudo-elements, so a selector with one keeps its own weight. */
const PSEUDO_ELEMENT = /::/;

/**
 * Wraps what follows the scope prefix in `:where()`, so the rule weighs exactly the prefix:
 * more than any element selector, less than any rule the document writes under the same
 * prefix. Standing in for a cascade layer, which the editor cannot use — see scopeRules.
 */
const softened = (scopePrefix: string, joiner: string, rest: string, soften: boolean): string => {
  if (!soften || PSEUDO_ELEMENT.test(rest)) return `${scopePrefix}${joiner}${rest}`;
  const combinator = rest.match(/^([>+~])\s*(.*)$/);
  return combinator
    ? `${scopePrefix} ${combinator[1]} :where(${combinator[2]})`
    : `${scopePrefix}${joiner}:where(${rest})`;
};

/**
 * prosemirror-tables draws every row inside the <tbody> and marks the header rows instead, so
 * a `thead …` rule has to name the marked rows to reach the cells. A bare `thead` rule is left
 * alone: the editor has no element for it to style.
 */
const HEAD_ROW = 'tr[data-table-section="head"]';

const inEditorTables = (selector: string): string =>
  selector
    .replace(/(^|[\s>+~(])thead(?:\s*>\s*|\s+)tr(?![\w-])/g, `$1${HEAD_ROW}`)
    .replace(/(^|[\s>+~(])thead(?=\s*>\s*\S|\s+[^\s>+~,)])/g, `$1${HEAD_ROW}`);

/** Commas inside functions, attribute values or escapes belong to a single selector. */
const splitSelectors = (selectors: string): string[] => {
  const result: string[] = [];
  let start = 0;
  let depth = 0;
  let quote = '';
  for (let i = 0; i < selectors.length; i++) {
    const char = selectors[i];
    if (char === '\\') {
      i++;
      continue;
    }
    if (quote) {
      if (char === quote) quote = '';
      continue;
    }
    if (char === '"' || char === "'") {
      quote = char;
      continue;
    }
    if (char === '(' || char === '[') depth++;
    else if (char === ')' || char === ']') depth--;
    else if (char === ',' && depth === 0) {
      result.push(selectors.slice(start, i));
      start = i + 1;
    }
  }
  result.push(selectors.slice(start));
  return result;
};

/**
 * Exported for its own tests: scopeCSS needs constructable stylesheets, which jsdom does not
 * implement, so it returns the CSS untouched there and can only be checked in a browser.
 */
export const scopeSelector = (
  selector: string,
  scopePrefix: string,
  rootStandsIn: boolean,
  soften = false
): string => {
  if (selector.startsWith(scopePrefix)) {
    return selector;
  }
  const rewritten = rootStandsIn ? inEditorTables(selector) : selector;
  if (!rootStandsIn || !DOCUMENT_ROOT.test(rewritten)) {
    return softened(scopePrefix, ' ', rewritten, soften);
  }

  // `html body` names the root twice, so strip for as long as it keeps naming it.
  let rest = rewritten;
  while (DOCUMENT_ROOT.test(rest)) {
    rest = rest.replace(DOCUMENT_ROOT, '');
  }
  // The document's own `body` rule is scoped to the bare prefix too, so a softened default
  // there has to weigh less than the prefix or it ties and, injected later, wins.
  if (!rest) {
    return soften ? `:where(${scopePrefix})` : scopePrefix;
  }
  return softened(scopePrefix, CONTINUES_ELEMENT.test(rest) ? '' : ' ', rest, soften);
};

/**
 * A stylesheet reduced to what scoping needs: style rules to rewrite, grouping rules to
 * descend into, and everything else to copy through. Independent of the CSSOM so the
 * traversal can be tested where the CSSOM is missing.
 */
export type ScopableRule =
  | { kind: 'style'; selectorText: string; declarations: string }
  | { kind: 'group'; prelude: string; rules: ScopableRule[] }
  | { kind: 'verbatim'; cssText: string };

const LAYER_PRELUDE = /^@layer\b/;

/**
 * flattenLayers unwraps every `@layer` block into unlayered rules whose selectors weigh only
 * the scope prefix. The renderer puts a document's defaults in a layer so anything the
 * document says beats them, but in the editor Tailwind's preflight is unlayered and beats
 * every layer, so the default cell border and list indent never showed. Unlayered at the
 * prefix's weight they beat preflight's element selectors and still lose to the document's
 * own rules, which carry the prefix and a selector besides.
 */
export const scopeRules = (
  rules: ScopableRule[],
  scopePrefix: string,
  rootStandsIn: boolean,
  flattenLayers = false,
  soften = false
): string =>
  rules
    .map((rule) => {
      switch (rule.kind) {
        case 'style': {
          const scopedSelector = splitSelectors(rule.selectorText)
            .map((s) => scopeSelector(s.trim(), scopePrefix, rootStandsIn, soften))
            .join(', ');
          return `${scopedSelector} { ${rule.declarations} }`;
        }
        case 'group': {
          if (flattenLayers && LAYER_PRELUDE.test(rule.prelude)) {
            return scopeRules(rule.rules, scopePrefix, rootStandsIn, flattenLayers, true);
          }
          return `${rule.prelude} { ${scopeRules(rule.rules, scopePrefix, rootStandsIn, flattenLayers, soften)} }`;
        }
        case 'verbatim':
          return rule.cssText;
      }
    })
    .join(' ');

const isInstance = <T>(ctor: (new () => T) | undefined, value: unknown): value is T =>
  ctor !== undefined && value instanceof ctor;

const toScopableRules = (rules: CSSRuleList): ScopableRule[] =>
  Array.from(rules, (rule): ScopableRule => {
    if (isInstance(globalThis.CSSStyleRule, rule)) {
      return { kind: 'style', selectorText: rule.selectorText, declarations: rule.style.cssText };
    }
    if (isInstance(globalThis.CSSMediaRule, rule)) {
      return {
        kind: 'group',
        prelude: `@media ${rule.conditionText}`,
        rules: toScopableRules(rule.cssRules)
      };
    }
    if (isInstance(globalThis.CSSSupportsRule, rule)) {
      return {
        kind: 'group',
        prelude: `@supports ${rule.conditionText}`,
        rules: toScopableRules(rule.cssRules)
      };
    }
    if (isInstance(globalThis.CSSLayerBlockRule, rule)) {
      return {
        kind: 'group',
        prelude: rule.name ? `@layer ${rule.name}` : '@layer',
        rules: toScopableRules(rule.cssRules)
      };
    }
    return { kind: 'verbatim', cssText: rule.cssText };
  });

const scopeCSSUnder = (
  css: string,
  scopePrefix: string,
  rootStandsIn: boolean,
  flattenLayers: boolean
): string => {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    return scopeRules(toScopableRules(sheet.cssRules), scopePrefix, rootStandsIn, flattenLayers);
  } catch {
    return css;
  }
};

/**
 * Scope CSS selectors under `[data-editor-scope="…"]` so template `body {}` rules
 * apply inside the editor root (TipTap has no `<body>` in the DOM).
 *
 * rootStandsIn maps document-root selectors to the editor root itself, and `thead` rules to
 * the header rows the editor keeps in its <tbody>. That rewrite is not reversible through
 * unscopeCSS, so callers must retain the original CSS for persistence. StyleNode does this by
 * serializing its raw css attribute instead of the scoped stylesheet injected by its node view.
 */
export const scopeCSS = (
  css: string,
  scopeId: string,
  rootStandsIn = false,
  flattenLayers = false
): string => scopeCSSUnder(css, `[data-editor-scope="${scopeId}"]`, rootStandsIn, flattenLayers);

/**
 * Set on the editor root once the backend stylesheet lands. That sheet leads with the document's
 * own styles as the PDF prints them, repaired or restyled under a design, so from then on it
 * stands in for the editor's copies of them.
 */
export const BACKEND_STYLED_ATTRIBUTE = 'data-editor-backend-styled';

/**
 * The scope of a `<style>` block in the document body: the editor root until the backend
 * stylesheet lands. `:where()` gives the guard no weight, so until then the block cascades as
 * the document's other styles do.
 */
export const bodyStyleScope = (scopeId: string): string =>
  `[data-editor-scope="${scopeId}"]:where(:not([${BACKEND_STYLED_ATTRIBUTE}]))`;

/** scopeCSS for a `<style>` block in the document body, under bodyStyleScope. */
export const scopeBodyStyleCSS = (css: string, scopeId: string): string =>
  scopeCSSUnder(css, bodyStyleScope(scopeId), true, false);

/** Strip the editor scope prefix, body-style guard included, before persisting CSS in HTML. */
export const unscopeCSS = (css: string): string => {
  const scopePattern =
    /\[data-editor-scope="[^"]+"\](?::where\(:not\(\[data-editor-backend-styled\]\)\))?\s*/g;
  return css.replace(scopePattern, '');
};

/** Matches every `<style>...</style>` block in an HTML string (head or body). */
export const STYLE_TAG_REGEX = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;

export const normalizeCSSBlock = (css: string): string => css.replace(/\s+/g, ' ').trim();

/** Normalized CSS bodies from every `<style>` in an HTML fragment. */
export const extractStyleCSSBlocks = (html: string): Set<string> => {
  const set = new Set<string>();
  for (const m of html.matchAll(STYLE_TAG_REGEX)) {
    const normalized = normalizeCSSBlock(m[1]);
    if (normalized) set.add(normalized);
  }
  return set;
};
